import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  History,
  Image,
  Mic,
  FileText,
  Video,
  Search,
  Heart,
  HeartOff,
  Download,
  Share2,
  Trash2,
  Calendar,
  Clock,
  Filter,
  Grid,
  List,
  Star,
  Copy,
  ExternalLink,
  Folder,
  FolderPlus
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

interface GeneratedItem {
  id: string;
  type: string;
  prompt: string;
  url: string;
  created_at: string;
  is_favorite?: boolean;
  title?: string;
  metadata?: any;
}

interface Collection {
  id: string;
  name: string;
  color: string;
  items: string[];
}

const GenerationHistory = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<GeneratedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [collections, setCollections] = useState<Collection[]>([
    { id: '1', name: 'Marketing', color: 'bg-blue-500', items: [] },
    { id: '2', name: 'Social Media', color: 'bg-pink-500', items: [] },
    { id: '3', name: 'Projects', color: 'bg-green-500', items: [] }
  ]);

  useEffect(() => {
    if (user?.id) {
      loadHistory();
    }
  }, [user?.id]);

  const loadHistory = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('generated_content')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Failed to load generation history');
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (item: GeneratedItem) => {
    try {
      const { error } = await supabase
        .from('generated_content')
        .update({ is_favorite: !item.is_favorite })
        .eq('id', item.id);

      if (error) throw error;

      setItems(items.map(i => 
        i.id === item.id ? { ...i, is_favorite: !i.is_favorite } : i
      ));
      
      toast.success(item.is_favorite ? 'Removed from favorites' : 'Added to favorites');
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  const deleteItem = async (item: GeneratedItem) => {
    if (!confirm('Are you sure you want to delete this item?')) return;

    try {
      const { error } = await supabase
        .from('generated_content')
        .delete()
        .eq('id', item.id);

      if (error) throw error;

      setItems(items.filter(i => i.id !== item.id));
      toast.success('Item deleted');
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const shareItem = async (item: GeneratedItem) => {
    const shareText = `Check out what I created with Genova AI! ${item.prompt.slice(0, 100)}...`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Genova AI Creation',
          text: shareText,
          url: item.url
        });
      } catch (error) {
        copyToClipboard(item.url);
      }
    } else {
      copyToClipboard(item.url);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard!');
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'image': return <Image className="w-4 h-4" />;
      case 'voice': return <Mic className="w-4 h-4" />;
      case 'script': return <FileText className="w-4 h-4" />;
      case 'video': return <Video className="w-4 h-4" />;
      default: return <Image className="w-4 h-4" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'image': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'voice': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'script': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
      case 'video': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.prompt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = activeFilter === 'all' || item.type === activeFilter;
    const matchesFavorites = !showFavoritesOnly || item.is_favorite;
    return matchesSearch && matchesFilter && matchesFavorites;
  });

  const stats = {
    total: items.length,
    images: items.filter(i => i.type === 'image').length,
    voices: items.filter(i => i.type === 'voice').length,
    scripts: items.filter(i => i.type === 'script').length,
    videos: items.filter(i => i.type === 'video').length,
    favorites: items.filter(i => i.is_favorite).length
  };

  return (
    <Card className="border-0 shadow-xl rounded-3xl overflow-hidden bg-card">
      <CardHeader className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 p-6">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <History className="w-6 h-6 text-white" />
            </div>
            <div className="text-white">
              <h3 className="text-xl sm:text-2xl font-bold">Generation History</h3>
              <p className="text-white/70 text-sm font-normal mt-1">
                {stats.total} total generations • {stats.favorites} favorites
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
              onClick={() => setViewMode('grid')}
              className="text-white"
            >
              <Grid className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              onClick={() => setViewMode('list')}
              className="text-white"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="p-6 space-y-6">
        {/* Stats Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: 'Images', count: stats.images, icon: Image, color: 'text-blue-500' },
            { label: 'Voices', count: stats.voices, icon: Mic, color: 'text-green-500' },
            { label: 'Scripts', count: stats.scripts, icon: FileText, color: 'text-orange-500' },
            { label: 'Videos', count: stats.videos, icon: Video, color: 'text-purple-500' },
            { label: 'Favorites', count: stats.favorites, icon: Star, color: 'text-yellow-500' }
          ].map((stat) => (
            <div 
              key={stat.label}
              className="flex items-center gap-2 p-3 bg-muted/30 rounded-xl"
            >
              <stat.icon className={`w-5 h-5 ${stat.color}`} />
              <div>
                <p className="text-lg font-bold">{stat.count}</p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search generations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-11 border-2 border-border rounded-xl"
            />
          </div>
          
          <div className="flex gap-2">
            <Button
              variant={showFavoritesOnly ? 'default' : 'outline'}
              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              className="rounded-xl"
            >
              <Heart className={`w-4 h-4 mr-2 ${showFavoritesOnly ? 'fill-current' : ''}`} />
              Favorites
            </Button>
          </div>
        </div>

        {/* Type Filters */}
        <Tabs value={activeFilter} onValueChange={setActiveFilter}>
          <TabsList className="w-full justify-start bg-muted/30 rounded-xl p-1 overflow-x-auto">
            <TabsTrigger value="all" className="rounded-lg">All</TabsTrigger>
            <TabsTrigger value="image" className="rounded-lg flex items-center gap-1">
              <Image className="w-3 h-3" /> Images
            </TabsTrigger>
            <TabsTrigger value="voice" className="rounded-lg flex items-center gap-1">
              <Mic className="w-3 h-3" /> Voices
            </TabsTrigger>
            <TabsTrigger value="script" className="rounded-lg flex items-center gap-1">
              <FileText className="w-3 h-3" /> Scripts
            </TabsTrigger>
            <TabsTrigger value="video" className="rounded-lg flex items-center gap-1">
              <Video className="w-3 h-3" /> Videos
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Collections */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold flex items-center gap-2">
              <Folder className="w-4 h-4" />
              Collections
            </h4>
            <Button variant="ghost" size="sm" className="text-xs">
              <FolderPlus className="w-3 h-3 mr-1" />
              New
            </Button>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {collections.map((col) => (
              <Badge 
                key={col.id}
                variant="outline" 
                className="cursor-pointer hover:bg-muted whitespace-nowrap"
              >
                <div className={`w-2 h-2 rounded-full ${col.color} mr-2`} />
                {col.name}
                <span className="ml-2 text-muted-foreground">({col.items.length})</span>
              </Badge>
            ))}
          </div>
        </div>

        {/* Items Grid/List */}
        <ScrollArea className="h-[500px] pr-4">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
              <History className="w-12 h-12 mb-3 opacity-50" />
              <p className="font-medium">No generations found</p>
              <p className="text-sm">Start creating to see your history here!</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredItems.map((item) => (
                <Card 
                  key={item.id}
                  className="group overflow-hidden bg-muted/30 border-border/50 hover:shadow-lg transition-all duration-300"
                >
                  <div className="relative aspect-square">
                    {item.type === 'image' || item.type === 'video' ? (
                      <img 
                        src={item.url} 
                        alt={item.prompt}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/50">
                        {getTypeIcon(item.type)}
                      </div>
                    )}
                    
                    {/* Overlay Actions */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => toggleFavorite(item)}
                        className="text-white hover:text-primary"
                      >
                        <Heart className={`w-5 h-5 ${item.is_favorite ? 'fill-red-500 text-red-500' : ''}`} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => shareItem(item)}
                        className="text-white hover:text-primary"
                      >
                        <Share2 className="w-5 h-5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => window.open(item.url, '_blank')}
                        className="text-white hover:text-primary"
                      >
                        <ExternalLink className="w-5 h-5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => deleteItem(item)}
                        className="text-white hover:text-red-500"
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>

                    {/* Favorite Badge */}
                    {item.is_favorite && (
                      <div className="absolute top-2 right-2">
                        <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                      </div>
                    )}

                    {/* Type Badge */}
                    <Badge className={`absolute top-2 left-2 ${getTypeColor(item.type)}`}>
                      {getTypeIcon(item.type)}
                    </Badge>
                  </div>
                  
                  <CardContent className="p-3">
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                      {item.prompt}
                    </p>
                    <div className="flex items-center text-xs text-muted-foreground">
                      <Clock className="w-3 h-3 mr-1" />
                      {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredItems.map((item) => (
                <Card 
                  key={item.id}
                  className="group overflow-hidden bg-muted/30 border-border/50 hover:shadow-lg transition-all"
                >
                  <CardContent className="p-4 flex items-center gap-4">
                    {/* Thumbnail */}
                    <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                      {item.type === 'image' || item.type === 'video' ? (
                        <img 
                          src={item.url} 
                          alt={item.prompt}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/50">
                          {getTypeIcon(item.type)}
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={getTypeColor(item.type)} variant="secondary">
                          {item.type}
                        </Badge>
                        {item.is_favorite && (
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        )}
                      </div>
                      <p className="text-sm truncate">{item.prompt}</p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(item.created_at), 'MMM d, yyyy • h:mm a')}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button size="icon" variant="ghost" onClick={() => toggleFavorite(item)}>
                        <Heart className={`w-4 h-4 ${item.is_favorite ? 'fill-red-500 text-red-500' : ''}`} />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => shareItem(item)}>
                        <Share2 className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => deleteItem(item)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default GenerationHistory;
